import { useHierarchyArea } from '@plarium/hierarchy-react';
import React, { useMemo } from 'react';

import isEmpty from 'lodash/isEmpty';

import { Section, Grid, Select } from '@gogame/components';
import { FormField, withFormFieldGroupFormField } from '@gogame/form';
import { dateFNS, fromTimeZoneToUTC, fromUTCToTimeZone } from '@gogame/intl-ts';

import { loadCurrencies } from 'scenes/Influencers/InfluencersCampaigns/sources/fields';

import {
  COST_MODEL,
  COST_MODEL_END_DATE,
  COST_MODEL_START_DATE,
  COST_MODEL_ID,
  CURRENCY,
  PAYOUT,
} from '../constants';
import Events from './Events';

const CostModel = ({
  name,
  isEdit,
  source,
  depends,
  active,
  value,
  formik,
}) => {
  const { data } = useHierarchyArea({
    name: name,
    source: source,
    depends: depends,
    active: active,
    normalizeValue: (model, models) => {
      let newModel = models.some((m) => m.id === model?.[COST_MODEL_ID])
        ? model
        : {
            [COST_MODEL_ID]: models[0].id,
            [PAYOUT]: model?.[PAYOUT],
            [CURRENCY]: model?.[CURRENCY],
          };

      return changeModel(newModel, models);
    },
  });

  const costModelExtraOptions = useMemo(() => {
    if (!value?.[COST_MODEL_ID]) {
      return {};
    }

    const option = (data || []).find((el) => el.id === value[COST_MODEL_ID]);
    let result = { ...option };

    if (result.startDate) {
      const startDateMasterCampaign = fromUTCToTimeZone(result.startDate);

      const year = startDateMasterCampaign.getFullYear();
      const month = startDateMasterCampaign.getMonth();

      if (result.isStartDateSupported) {
        result.startDateLimits = [
          fromTimeZoneToUTC(new Date(year, month, 1, 0, 0, 1)),
        ];
      }

      if (result.isEndDateSupported && !result.isUnrestrictedDates) {
        result.endDateLimits = [
          new Date(year, month + 1, 0),
          new Date(year, month + 1, 7),
        ];
      }
    }
    return result;
  }, [value?.[COST_MODEL_ID], data]);

  const handleChangeModel = (_, currentModelId) => {
    formik.setFieldValue(
      name,
      changeModel(
        {
          [COST_MODEL_ID]: currentModelId,
          [PAYOUT]: value?.[PAYOUT],
          [CURRENCY]: value?.[CURRENCY],
        },
        data
      )
    );
  };

  const changeModel = (model, options) => {
    let newModel = model;

    const {
      startDate,
      isStartDateSupported,
      isEndDateSupported,
      isUnrestrictedDates,
    } = options;

    if (startDate) {
      const startDateMasterCampaign = fromUTCToTimeZone(startDate);

      const year = startDateMasterCampaign.getFullYear();
      const month = startDateMasterCampaign.getMonth();

      if (isStartDateSupported) {
        newModel[COST_MODEL_START_DATE] = fromTimeZoneToUTC(
          new Date(year, month, 1, 0, 0, 1)
        );
      } else {
        delete newModel[COST_MODEL_START_DATE];
      }

      if (isEndDateSupported) {
        let endDateDefault = null;

        if (isUnrestrictedDates) {
          endDateDefault = fromTimeZoneToUTC(
            new Date(year, month + 1, 0, 23, 59, 59)
          );
        } else {
          endDateDefault = fromTimeZoneToUTC(
            new Date(year, month + 1, 0, 23, 59, 59)
          );
        }
        newModel[COST_MODEL_END_DATE] = endDateDefault;
      } else {
        delete newModel[COST_MODEL_END_DATE];
      }
    }
    return newModel;
  };

  const startDateMinDate = useMemo(() => {
    return costModelExtraOptions.startDateLimits?.[0];
  }, []);

  const startDateMaxDate = useMemo(() => {
    let endDate = value?.[COST_MODEL_END_DATE];
    if (endDate) {
      return dateFNS.subDays(new Date(endDate), 1);
    }
  }, []);

  if (isEmpty(data)) {
    return null;
  }

  return (
    <Section title={'Cost Model'}>
      <Grid container spacing={3}>
        <Grid item xs={4}>
          <Select
            options={data}
            label={'Cost Model'}
            name={COST_MODEL}
            value={value?.[COST_MODEL_ID]}
            onChange={handleChangeModel}
            data-cy={'costModelId'}
          />
        </Grid>
        <Grid item xs={4}>
          <FormField.DatePicker
            name={COST_MODEL_START_DATE}
            label={'Cost model start date'}
            minDate={startDateMinDate}
            maxDate={startDateMaxDate}
            padding={0}
            disabled={isEdit || !costModelExtraOptions.isStartDateSupported}
          />
        </Grid>
        <Grid item xs={4}>
          <FormField.DatePicker
            name={COST_MODEL_END_DATE}
            label={'Cost model end date'}
            minDate={costModelExtraOptions.endDateLimits?.[0]}
            maxDate={costModelExtraOptions.endDateLimits?.[1]}
            padding={0}
            disabled={isEdit || !costModelExtraOptions.isEndDateSupported}
          />
        </Grid>

        <Grid item xs={2}>
          <FormField.TextField
            type={'currency'}
            name={PAYOUT}
            label={'Payout / Rate'}
            padding={0}
            placeholder={'$0'}
            disabled={isEdit}
            currency={value?.[CURRENCY]}
            required={true}
          />
        </Grid>
        <Grid item xs={2}>
          <FormField.Select
            label={'Currency'}
            padding={0}
            name={CURRENCY}
            source={loadCurrencies}
            lazy={true}
            disabled={isEdit}
          />
        </Grid>
        {costModelExtraOptions.isEventSupported ? (
          <Grid item xs={4}>
            <Events disabled={isEdit} />
          </Grid>
        ) : null}
      </Grid>
    </Section>
  );
};

export const CostModelGroup = withFormFieldGroupFormField()(CostModel);
